import { AddCircle, CheckCircleOutline, ChevronRight } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridActionsCellItem, GridActionsCellItemProps, GridRowParams } from '@mui/x-data-grid-premium';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ROUTES } from 'routing';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import { GridColumns } from 'shared/types';
import { moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { AddDeviationsData } from 'types/backend';
import { CatalogProductsResolver } from './catalogProductsResolver';
import { DeviationFilters } from './deviationFilters';
import { ExclusiveProductsResolver } from './ExclusiveProductsResolver';
import { PrivateProductsResolver } from './PrivateProductsResolver';
import { SelectedProductsTable } from './selectedProductsTable';
import SetupDeviations from './setupDeviations';

interface AddDeviationsProps {
    customerName: string;
}
export function AddDeviations({ customerName }: AddDeviationsProps) {
    const [selected, setSelected] = useState<AddDeviationsData[]>([]);
    const handleClick = (params: AddDeviationsData) => () => {
        setSelected([...selected, params]);
    };

    const determineIcon: (
        params: GridRowParams<AddDeviationsData>
    ) => React.ReactElement<GridActionsCellItemProps, string | React.JSXElementConstructor<any>>[] = (params) => {
        return selected.map((selectedItem) => selectedItem.id).includes(params.row.id)
            ? [<GridActionsCellItem icon={<CheckCircleOutline />} label='Selected' />]
            : [<GridActionsCellItem icon={<AddCircle />} label='Select' onClick={handleClick(params.row)} />];
    };
    const columnConfig: GridColumns<AddDeviationsData> = [
        {
            headerName: 'Select Products',
            field: 'actions',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 200,
            getActions: determineIcon,
        },
        {
            headerName: 'Product',
            field: 'productId',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Product Group',
            field: 'group',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Pricing Group',
            field: 'priceGroup',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Type',
            field: 'type',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Gauge',
            field: 'gauge',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Length',
            field: 'length',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Width',
            field: 'width',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Tier 1 Target Price',
            field: 'tierOnePrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Customer Target Price',
            field: 'targetPrice',
            hideable: false,
            pinnable: false,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Market Price',
            field: 'price',
            hideable: false,
            pinnable: false,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
        },
    ];

    const toolbarChildren: JSX.Element[] = [
        <Box key={'toolbar-space-filler-upper'} sx={{ flexGrow: 1 }} />,
        <DeviationFilters key={'filters'} />,
    ];

    const continueHandler = UseDynamicUrl<AccountNumberParams>({ route: ROUTES.addDeviationsSetup });

    return (
        <Switch>
            <Route path={[ROUTES.addDeviationsSetup, ROUTES.addDeviationsConfirm]}>
                <SetupDeviations selectedProducts={selected} customerName={customerName} />
            </Route>
            <Route path={ROUTES.addDeviations}>
                <Switch>
                    <Route path={ROUTES.addDeviationsCatalog}>
                        <CatalogProductsResolver columnConfig={columnConfig} toolbarChildren={toolbarChildren} />
                    </Route>

                    <Route path={ROUTES.addDeviationsExclusive}>
                        <ExclusiveProductsResolver columnConfig={columnConfig} toolbarChildren={toolbarChildren} />
                    </Route>

                    <Route path={ROUTES.addDeviationsPrivate}>
                        <PrivateProductsResolver columnConfig={columnConfig} toolbarChildren={toolbarChildren} />
                    </Route>
                </Switch>
                <SelectedProductsTable selectedProducts={selected} setSelected={setSelected} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant={'contained'}
                        sx={{
                            backgroundColor: 'secondary.main',
                            color: 'black',
                            '&:hover': {
                                backgroundColor: 'secondary.light',
                                color: 'black',
                            },
                        }}
                        onClick={continueHandler.clickHandler}
                        endIcon={<ChevronRight />}
                        disabled={selected.length === 0}
                    >
                        Continue
                    </Button>
                </Box>
            </Route>
        </Switch>
    );
}
